import React, { useEffect, useMemo } from 'react';
import {
  Trophy, Lock, CheckCircle, Building2, Briefcase, Coffee, Sparkles,
  Truck, User, Users, Heart, TrendingUp, Wallet, FileText, Star, Award,
  Warehouse, MapPin, Gift, Crown,
} from 'lucide-react';
import useGameStore from '../store/gameStore';
import { ACHIEVEMENTS, TIER_STYLES, CATEGORIES } from '../data/achievements';
import { useTranslation } from '../i18n';

const ICONS = {
  Building2, Briefcase, Coffee, Sparkles,
  Truck, User, Users, Heart, TrendingUp, Wallet,
  CheckCircle, FileText, Star, Award, Warehouse, MapPin, Gift, Crown,
};

/**
 * Carriere — achievements / medals page.
 * Lists all milestones grouped by category, with locked/unlocked state.
 * Marks all newly-unlocked achievements as viewed when the page loads.
 *
 * 29/05/2026 — Achievements feature.
 */
export default function Achievements() {
  const { t } = useTranslation();
  const unlocked = useGameStore((s) => s.unlockedAchievements || {});
  const markAllAchievementsViewed = useGameStore((s) => s.markAllAchievementsViewed);
  const getAchievementProgress = useGameStore((s) => s.getAchievementProgress);

  useEffect(() => {
    if (typeof markAllAchievementsViewed === 'function') markAllAchievementsViewed();
  }, [markAllAchievementsViewed]);

  const progress = useMemo(
    () => (getAchievementProgress ? getAchievementProgress() : { unlockedCount: 0, totalCount: 0, byCategory: {} }),
    // `unlocked` is intentionally listed even though `getAchievementProgress`
    // is the only direct dependency: `unlocked` is the underlying state slice
    // and we want to recompute when it changes. The lint rule cannot infer
    // that the helper closes over it, so we suppress here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAchievementProgress, unlocked]
  );

  const grouped = useMemo(() => {
    const out = {};
    for (const ach of ACHIEVEMENTS) {
      if (!out[ach.category]) out[ach.category] = [];
      out[ach.category].push(ach);
    }
    return out;
  }, []);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6" data-testid="achievements-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-100 flex items-center gap-3">
            <Trophy className="w-7 h-7 text-amber-400" /> Médailles  Carrière Carrière
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Vos médailles débloquées au fil de la partie. Chaque jalon raconte une étape de votre aventure.
          </p>
        </div>
        <div className="bg-ink-800 border border-ink-600 rounded-lg px-4 py-2 text-center" data-testid="achievements-progress">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Progression</p>
          <p className="text-2xl font-black text-amber-300 font-mono">
            {progress.unlockedCount}
            <span className="text-slate-500 text-base"> / {progress.totalCount}</span>
          </p>
          <p className="text-[10px] text-slate-500">
            {progress.totalCount > 0
              ? `${Math.round((progress.unlockedCount / progress.totalCount) * 100)} % ${t('achievements_unlocked_pct')}`
              : '—'}
          </p>
        </div>
      </div>

      {/* Grille par catégorie */}
      {CATEGORIES.map((cat) => {
        const items = grouped[cat] || [];
        if (items.length === 0) return null;
        const stat = progress.byCategory[cat] || { unlocked: 0, total: items.length };
        return (
          <section key={cat} data-testid={`achievements-cat-${cat.toLowerCase()}`}>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-base font-bold text-slate-200">{cat}</h2>
              <span className="text-xs font-mono text-slate-500">
                {stat.unlocked} / {stat.total}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((ach) => {
                const isUnlocked = !!unlocked[ach.id];
                const tier = TIER_STYLES[ach.tier] || TIER_STYLES.bronze;
                const Icon = ICONS[ach.icon] || Trophy;
                return (
                  <div
                    key={ach.id}
                    data-testid={`achievement-${ach.id}`}
                    className={`relative rounded-lg border p-3 transition-all ${
                      isUnlocked
                        ? `bg-gradient-to-br ${tier.bg} border-slate-500/50`
                        : 'bg-ink-800 border-ink-600 opacity-60'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isUnlocked ? 'bg-ink-900/60' : 'bg-ink-700/40'
                        }`}
                        style={isUnlocked ? { boxShadow: `0 0 12px ${tier.color}40` } : {}}
                      >
                        {isUnlocked ? (
                          <Icon className="w-6 h-6" style={{ color: tier.color }} />
                        ) : (
                          <Lock className="w-5 h-5 text-slate-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className={`text-sm font-bold truncate ${isUnlocked ? 'text-slate-100' : 'text-slate-500'}`}>
                            {isUnlocked ? ach.name : '???'}
                          </p>
                          <span
                            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded font-mono whitespace-nowrap"
                            style={{ color: tier.color, backgroundColor: `${tier.color}15` }}
                          >
                            {tier.label}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                          {isUnlocked ? ach.description : ach.hint}
                        </p>
                        {isUnlocked && unlocked[ach.id]?.unlockedAt != null && (
                          <p className="text-[10px] text-slate-500 mt-1 font-mono">
                            Débloquée jour {unlocked[ach.id].unlockedAt}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
